/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import { Range } from 'immutable';
import { createContext, useContext, useEffect, useState } from 'react';
import * as checked from 'reporting-data/lib/checked';
import { DatasetMeta, resolveDatasetMeta } from '../../../../dataset/dataset-meta';
import { RelationalDataset } from '../../../../dataset/dataset-records';
import { toPropertyBasicType } from '../../../../dataset/type-utils';
import { resolveReportMeta } from '../../../metadata/report-meta';
import { RelationalReport } from '../../../schema/report-records';
import { TableAndPropertyMeta } from '../../../schema/source-records';
import { expressionFieldsListToMap } from '../../../utils/expression-field-utils';
import { jsx as _jsx } from "react/jsx-runtime";
const SourceMetaContext = /*#__PURE__*/createContext();
const ReportWithMeta = checked.record({
  schema: checked.string().always('report'),
  report: RelationalReport,
  reportMeta: TableAndPropertyMeta,
  dataset: RelationalDataset.optional(),
  datasetMeta: DatasetMeta.optional()
});
const DatasetWithMeta = checked.record({
  schema: checked.string().always('dataset'),
  dataset: RelationalDataset,
  datasetMeta: DatasetMeta
});
const SourceMeta = checked.poly('schema', {
  report: ReportWithMeta,
  dataset: DatasetWithMeta
}, 'SourceMetaProvider');
const fetchDatasetWithMeta = dataset => resolveDatasetMeta(dataset).then(datasetMeta => ({
  dataset,
  datasetMeta
}));
const fetchReportWithMeta = report => resolveReportMeta(report).then(reportMeta => reportMeta.datasetDefinition ? fetchDatasetWithMeta(reportMeta.datasetDefinition).then(datasetWithMeta => Object.assign({
  report,
  reportMeta
}, datasetWithMeta)) : {
  report,
  reportMeta
});
export const SourceMetaProvider = ({
  schema,
  initialDefinition,
  children
}) => {
  const [sourceMeta, setSourceMeta] = useState();
  useEffect(() => {
    let expired = false;
    setSourceMeta();
    const maybeSetMeta = meta => {
      if (!expired) {
        setSourceMeta(SourceMeta(Object.assign({
          schema
        }, meta)));
      }
    };
    switch (schema) {
      case 'report':
        {
          fetchReportWithMeta(initialDefinition).then(maybeSetMeta);
          break;
        }
      case 'dataset':
        {
          fetchDatasetWithMeta(initialDefinition).then(maybeSetMeta);
          break;
        }
      default:
        throw new Error(`Could not resolve metadata for unknown schema ${schema}`);
    }
    return () => expired = true;
  }, [schema, initialDefinition]);
  return /*#__PURE__*/_jsx(SourceMetaContext.Provider, {
    value: sourceMeta,
    children: children
  });
};
class UnknownSchemaError extends Error {}
export const useSourceMeta = () => {
  // todo error handling
  return useContext(SourceMetaContext);
};
export const useFiltering = () => {
  const sourceMeta = useSourceMeta();
  if (!sourceMeta) {
    return undefined;
  }
  const {
    schema
  } = sourceMeta;
  if (schema === 'report') {
    return sourceMeta.report.filtering;
  }
  if (schema === 'dataset') {
    return sourceMeta.dataset.filtering;
  }
  throw new UnknownSchemaError(`Unknown schema ${schema}`);
};

/**
 * Assumes report definition has been successfully loaded at call time.
 */
export const useGroupIndices = () => {
  const {
    groups
  } = useFiltering();
  const groupCount = groups ? groups.count() : 0;
  return Range(0, groupCount).toSet();
};
export const useEventDateInterval = () => {
  const sourceMeta = useSourceMeta();
  if (!sourceMeta) {
    return undefined;
  }
  const {
    schema
  } = sourceMeta;
  if (schema === 'report') {
    return sourceMeta.report.eventDateInterval;
  }
  if (schema === 'dataset') {
    return sourceMeta.dataset.eventDateInterval;
  }
  throw new UnknownSchemaError(`Unknown schema ${schema}`);
};
export const useTableDescription = () => {
  const sourceMeta = useSourceMeta();
  if (!sourceMeta) {
    return undefined;
  }
  const {
    schema
  } = sourceMeta;
  if (schema === 'report') {
    return sourceMeta.report.table;
  }
  if (schema === 'dataset') {
    return sourceMeta.dataset.table;
  }
  throw new UnknownSchemaError(`Unknown schema ${schema}`);
};

/**
 * @returns {Map<string, SnowflakeTable>}
 */
export const useTableSources = () => {
  const sourceMeta = useSourceMeta();
  if (!sourceMeta) {
    return undefined;
  }
  const {
    schema
  } = sourceMeta;
  if (schema === 'report') {
    return sourceMeta.reportMeta.tables;
  }
  if (schema === 'dataset') {
    return sourceMeta.datasetMeta.tables;
  }
  throw new UnknownSchemaError(`Unknown schema ${schema}`);
};

/**
 * @param tableName - string
 * @returns {SnowflakeTable?} - optional
 */
export const useTableSource = tableName => {
  const sourceMeta = useSourceMeta();
  if (!sourceMeta) {
    return undefined;
  }
  const {
    schema
  } = sourceMeta;
  if (schema === 'report') {
    return sourceMeta.getIn(['reportMeta', 'tables', tableName]);
  }
  if (schema === 'dataset') {
    return sourceMeta.getIn(['datasetMeta', 'tables', tableName]);
  }
  throw new UnknownSchemaError(`Unknown schema ${schema}`);
};

/**
 * @param tableName - string
 * @param fieldName - string
 * @returns {SnowflakeProperty?} - optional
 */
export const useProperty = (tableName, fieldName) => {
  const sourceMeta = useSourceMeta();
  if (!sourceMeta) {
    return undefined;
  }
  const {
    schema
  } = sourceMeta;
  if (schema === 'report') {
    return sourceMeta.getIn(['reportMeta', 'properties', tableName, fieldName]);
  }
  if (schema === 'dataset') {
    return sourceMeta.getIn(['datasetMeta', 'properties', tableName, fieldName]);
  }
  throw new UnknownSchemaError(`Unknown schema ${schema}`);
};
export const useDatasetDefinition = () => {
  const sourceMeta = useSourceMeta();
  if (!sourceMeta) {
    return undefined;
  }
  return sourceMeta.dataset;
};
export const useReportDefinition = () => {
  const sourceMeta = useSourceMeta();
  if (!sourceMeta) {
    return undefined;
  }
  return sourceMeta.report;
};
export const useField = fieldName => {
  const datasetDefinition = useDatasetDefinition();
  return datasetDefinition && datasetDefinition.getIn(['fields', fieldName]);
};
export const useExpressionField = fieldAlias => {
  const report = useReportDefinition();
  return report && report.expressions && expressionFieldsListToMap(report.expressions).get(fieldAlias);
};
export const useExpressionFields = () => {
  const report = useReportDefinition();
  return report && report.expressions;
};
export const useFieldBasicType = fieldName => {
  const field = useField(fieldName);
  if (!field) {
    return undefined;
  }
  return toPropertyBasicType(field.type);
};
export const useMeta = () => {
  const sourceMeta = useSourceMeta();
  const {
    schema
  } = sourceMeta;
  if (schema === 'report') {
    return sourceMeta.reportMeta;
  }
  if (schema === 'dataset') {
    return sourceMeta.datasetMeta;
  }
  throw new UnknownSchemaError(`Unknown schema ${schema}`);
};